import React, { useState } from 'react';
import { arrayOf, bool, func, node, object, shape, string } from 'prop-types';
import classNames from 'classnames';

import { propTypes } from '../../util/types';
import StoreSidebar from '../StoreSidebar/StoreSidebar';
import CompactListingCard from '../ListingCard/CompactListingCard';
import ItemSourceToggle from '../ItemSourceToggle/ItemSourceToggle';
import BrandCarousel from '../BrandCarousel/BrandCarousel';

import css from '../../containers/StorePage/StorePage.module.css';

const CatalogSearchContent = props => {
  const {
    categories,
    catalogLabel,
    catalogMode,
    currentCategory,
    currentCategoryLabel,
    currentSort,
    currentSubcategoryLabel,
    emptyMessage,
    fetchingListings,
    intl,
    listings,
    manufacturerImages,
    manufacturerImagesInProgress,
    mobileMenuOpen,
    onCategoryClick,
    onParamChange,
    onResetAll,
    onSortChange,
    resultsFooter,
    showMobileTopbarSourceToggle,
    showMobileBrandBeforeControls,
    showMobileInlineSearch,
    searchKeyword,
    onSearchKeywordChange,
    onSearchSubmit,
    searchPlaceholder,
    showCategoryNavigation,
    showSourceToggle,
    sortOptions,
    urlQueryParams,
    setMobileMenuOpen,
    brands,
    onBrandSelect,
    availableBrandValues,
  } = props;

  const [isMobileModalOpen, setIsMobileModalOpen] = useState(false);
  const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

  const handleMobileSearchSubmit = e => {
    if (onSearchSubmit) onSearchSubmit(e);
    setIsMobileSearchOpen(false);
  };
  const hasListings = listings.length > 0;
  const showChannelToggle = showSourceToggle && catalogMode === 'products';
  const shouldRenderBrandCarousel = brands && brands.length > 0;
  const currentChannel = urlQueryParams?.channel === 'warehouse' ? 'warehouse' : 'in-store';
  const handleChannelChange = next =>
    onParamChange({ channel: next === 'warehouse' ? 'warehouse' : null });

  return (
    <>
      <div className={css.layoutContainer}>
        <StoreSidebar
          urlQueryParams={urlQueryParams}
          onParamChange={onParamChange}
          onResetAll={onResetAll}
          intl={intl}
          catalogMode={catalogMode}
        />

        <div className={css.mainContent}>
          {showCategoryNavigation && (currentCategory !== 'all' || currentSubcategoryLabel) && (
            <div className={css.breadcrumbs}>
              <span className={css.breadcrumbLink} onClick={() => onCategoryClick('all')}>
                {catalogLabel}
              </span>
              <span className={css.breadcrumbSeparator}>&gt;</span>
              {currentSubcategoryLabel ? (
                <>
                  <span
                    className={css.breadcrumbLink}
                    onClick={() => onCategoryClick(currentCategory)}
                  >
                    {currentCategoryLabel}
                  </span>
                  <span className={css.breadcrumbSeparator}>&gt;</span>
                  <span className={css.breadcrumbCurrent}>{currentSubcategoryLabel}</span>
                </>
              ) : (
                <span className={css.breadcrumbCurrent}>{currentCategoryLabel}</span>
              )}
            </div>
          )}

          <div className={css.mainHeader}>
            <h2 className={css.sectionHeading}>
              {showCategoryNavigation ? currentCategoryLabel : catalogLabel}
            </h2>
          </div>

          <div className={css.catalogDiscoveryControls}>
            <div
              className={classNames(css.mobileControlsWrap, {
                [css.mobileControlsWrapBrandAfter]: showMobileBrandBeforeControls,
              })}
            >
              <div className={css.controlsRow}>
                <div className={css.controlsRowLeft}>
                  {showMobileInlineSearch && (
                    <button
                      type="button"
                      className={classNames(css.mobileSearchButton, {
                        [css.mobileSearchButtonActive]: isMobileSearchOpen,
                      })}
                      aria-label="Search"
                      aria-expanded={isMobileSearchOpen}
                      onClick={() => setIsMobileSearchOpen(open => !open)}
                    >
                      <svg
                        className={css.mobileSearchIcon}
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="9" cy="9" r="6" />
                        <line x1="15" y1="15" x2="18" y2="18" />
                      </svg>
                    </button>
                  )}
                  <select className={css.mobileSortSelect} value={currentSort} onChange={onSortChange}>
                    {sortOptions.map(opt => (
                      <option key={opt.key} value={opt.key}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  className={css.filterResultsButton}
                  onClick={() => setIsMobileModalOpen(true)}
                >
                  Filter Results
                </button>
              </div>

              {showMobileInlineSearch && isMobileSearchOpen && (
                <>
                  <div
                    className={css.mobileSearchBackdrop}
                    onClick={() => setIsMobileSearchOpen(false)}
                  />
                  <form className={css.mobileSearchPanel} onSubmit={handleMobileSearchSubmit}>
                    <input
                      className={css.mobileSearchInput}
                      type="text"
                      autoFocus
                      value={searchKeyword || ''}
                      placeholder={searchPlaceholder}
                      onChange={e => onSearchKeywordChange && onSearchKeywordChange(e.target.value)}
                    />
                    <button className={css.mobileSearchGo} type="submit">
                      Go
                    </button>
                  </form>
                </>
              )}
            </div>

            {showChannelToggle && (
              <ItemSourceToggle
                className={showMobileTopbarSourceToggle ? css.desktopOnlySourceToggle : null}
                value={currentChannel}
                onChange={handleChannelChange}
              />
            )}

            {shouldRenderBrandCarousel && (
              <BrandCarousel
                className={classNames({
                  [css.brandCarouselMobileFirst]: showMobileBrandBeforeControls,
                })}
                title="Shop by brand"
                brands={brands}
                activeBrandName={urlQueryParams.pub_brand}
                onSelectBrand={onBrandSelect}
                availableBrandValues={availableBrandValues}
              />
            )}

            <div className={css.desktopSortRow}>
              <select className={css.headerSortBy} value={currentSort} onChange={onSortChange}>
                {sortOptions.map(opt => (
                  <option key={opt.key} value={opt.key}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isMobileModalOpen && (
            <div className={css.mobileFilterPanel} onClick={() => setIsMobileModalOpen(false)}>
              <div className={css.mobileFilterPanelInner} onClick={e => e.stopPropagation()}>
                <div className={css.mobileFilterPanelScroll}>
                  <div className={css.mobileFilterPanelHeader}>
                    <button
                      className={css.mobileFilterPanelClose}
                      onClick={() => setIsMobileModalOpen(false)}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        stroke="#1E1E1E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      >
                        <line x1="1" y1="1" x2="13" y2="13" />
                        <line x1="13" y1="1" x2="1" y2="13" />
                      </svg>
                    </button>
                  </div>

                  <div className={css.mobileFilterPanelContent}>
                    <StoreSidebar
                      urlQueryParams={urlQueryParams}
                      onParamChange={onParamChange}
                      onResetAll={onResetAll}
                      intl={intl}
                      isMobile
                      catalogMode={catalogMode}
                    />
                  </div>
                </div>

                <div className={css.mobileFilterPanelFooter}>
                  <button
                    className={css.applyFiltersButton}
                    onClick={() => setIsMobileModalOpen(false)}
                  >
                    Apply Filters
                  </button>
                </div>
              </div>
            </div>
          )}

          {hasListings ? (
            <ul id="listings" className={css.listings}>
              {listings.map(l => (
                <li className={css.listing} key={l.id.uuid}>
                  <CompactListingCard
                    listing={l}
                    manufacturerImages={manufacturerImages}
                    isIframe
                  />
                </li>
              ))}
            </ul>
          ) : fetchingListings ? (
            <p className={css.loaderHelper}>Fetching listings...</p>
          ) : (
            <p className={css.loaderHelper}>{emptyMessage}</p>
          )}

          {manufacturerImagesInProgress ? (
            <p className={css.loaderHelper}>Loading manufacturer images...</p>
          ) : null}

          {resultsFooter}
        </div>
      </div>

      {mobileMenuOpen && (
        <div className={css.mobileMenu}>
          <div className={css.mobileMenuHeader}>
            <button className={css.mobileMenuBack} onClick={() => setMobileMenuOpen(false)}>
              <svg
                width="5"
                height="9"
                viewBox="0 0 5 9"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1.05 4.45L4.95 8.4L4.45 8.9L0 4.45L4.45 0L4.95 0.5L1.05 4.4V4.45Z"
                  fill="#1E1E1E"
                />
              </svg>
              Back
            </button>
            <button className={css.mobileMenuClose} onClick={() => setMobileMenuOpen(false)}>
              Close
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                stroke="#1E1E1E"
                strokeWidth="2"
                strokeLinecap="round"
              >
                <line x1="1" y1="1" x2="13" y2="13" />
                <line x1="13" y1="1" x2="1" y2="13" />
              </svg>
            </button>
          </div>

          <div className={css.mobileMenuList}>
            <div
              className={css.mobileMenuItem}
              onClick={() => {
                onCategoryClick('all');
                setMobileMenuOpen(false);
              }}
            >
              <span className={css.mobileMenuLabel}>{catalogLabel}</span>
            </div>
            {showCategoryNavigation
              ? categories.map(cat => (
                  <div
                    key={cat.option}
                    className={css.mobileMenuItem}
                    onClick={() => {
                      onCategoryClick(cat.option);
                      setMobileMenuOpen(false);
                    }}
                  >
                    <span className={css.mobileMenuLabel}>{cat.label}</span>
                  </div>
                ))
              : null}
          </div>
        </div>
      )}
    </>
  );
};

CatalogSearchContent.defaultProps = {
  catalogLabel: 'All Products',
  catalogMode: 'products',
  currentSubcategoryLabel: null,
  emptyMessage: 'There are no listings matching your filters.',
  fetchingListings: false,
  manufacturerImages: {},
  manufacturerImagesInProgress: false,
  resultsFooter: null,
  showMobileTopbarSourceToggle: false,
  showMobileBrandBeforeControls: false,
  showMobileInlineSearch: false,
  searchKeyword: '',
  searchPlaceholder: "Tell us more about what you're looking for...",
  onSearchKeywordChange: null,
  onSearchSubmit: null,
  showCategoryNavigation: true,
  showSourceToggle: false,
  brands: null,
  onBrandSelect: null,
  availableBrandValues: null,
};

CatalogSearchContent.propTypes = {
  categories: arrayOf(
    shape({
      option: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  catalogLabel: string,
  catalogMode: string,
  currentCategory: string.isRequired,
  currentCategoryLabel: string.isRequired,
  currentSort: string.isRequired,
  currentSubcategoryLabel: string,
  emptyMessage: string,
  fetchingListings: bool,
  intl: object.isRequired,
  listings: arrayOf(propTypes.listing).isRequired,
  manufacturerImages: object,
  manufacturerImagesInProgress: bool,
  mobileMenuOpen: bool.isRequired,
  onCategoryClick: func.isRequired,
  onParamChange: func.isRequired,
  onResetAll: func.isRequired,
  onSortChange: func.isRequired,
  resultsFooter: node,
  showMobileTopbarSourceToggle: bool,
  showMobileBrandBeforeControls: bool,
  showMobileInlineSearch: bool,
  searchKeyword: string,
  searchPlaceholder: string,
  onSearchKeywordChange: func,
  onSearchSubmit: func,
  showCategoryNavigation: bool,
  showSourceToggle: bool,
  sortOptions: arrayOf(
    shape({
      key: string.isRequired,
      label: string.isRequired,
    })
  ).isRequired,
  urlQueryParams: object.isRequired,
  setMobileMenuOpen: func.isRequired,
  brands: arrayOf(object),
  onBrandSelect: func,
  availableBrandValues: arrayOf(string),
};

export default CatalogSearchContent;
